import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';

interface HeaderProps {
  onMenuClick: () => void;
}

const PAGE_TITLES: Record<string, string> = {
  '/dashboard':               'Dashboard',
  '/customers':               'Customers',
  '/vendors':                 'Vendors',
  '/jobs':                    'Jobs',
  '/invoices':                'Invoices',
  '/estimates':               'Estimates',
  '/bills':                   'Bills',
  '/purchase-orders':         'Purchase Orders',
  '/goods-receipts':          'Goods Receipts',
  '/payments':                'Payments',
  '/bank-accounts':           'Bank Accounts',
  '/reports':                 'Reports',
  '/products':                'Products',
  '/stock-transfer':          'Stock Transfer',
  '/service-stock':           'Service Stock',
  '/rentals':                 'Rentals',
  '/machine-reports':         'Machine Reports',
  '/settings/company':        'Company Settings',
  '/settings/invoice-templates': 'Invoice Templates',
  '/settings/users':          'User Management',
};

export const Header: React.FC<HeaderProps> = ({ onMenuClick }) => {
  const { user, company, logout } = useAuthStore();
  const [profileOpen, setProfileOpen] = useState(false);
  const location = useLocation();

  const pageTitle = Object.entries(PAGE_TITLES).find(([path]) =>
    location.pathname === path || (path !== '/dashboard' && location.pathname.startsWith(path))
  )?.[1] ?? 'Ledger Pro';

  const initials = `${user?.firstName?.[0] ?? ''}${user?.lastName?.[0] ?? ''}`.toUpperCase();

  return (
    <header style={{
      height: 64,
      background: '#f4efe2',
      borderBottom: '1px solid rgba(92,78,51,0.14)',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '0 32px',
      position: 'sticky', top: 0, zIndex: 30,
      flexShrink: 0,
    }}>
      {/* Left: mobile menu + page title */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        {/* Mobile hamburger */}
        <button
          onClick={onMenuClick}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: 36, height: 36, background: 'transparent',
            border: '1px solid #e3dcc4', borderRadius: 8, cursor: 'pointer',
            color: '#6b6a5c', transition: 'all 0.15s',
          }}
          onMouseEnter={e => {
            const el = e.currentTarget as HTMLElement;
            el.style.background = '#f4efe2';
            el.style.borderColor = '#d9d0b0';
            el.style.color = '#1c2033';
          }}
          onMouseLeave={e => {
            const el = e.currentTarget as HTMLElement;
            el.style.background = 'transparent';
            el.style.borderColor = '#e3dcc4';
            el.style.color = '#6b6a5c';
          }}
        >
          <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>

        {/* Page title */}
        <div>
          <div style={{
            fontFamily: "'Inter', sans-serif", fontSize: 11, fontWeight: 600,
            letterSpacing: '0.06em', textTransform: 'uppercase', color: '#8a8778',
            lineHeight: 1,
          }}>
            {company?.name ?? 'Ledger Pro'}
          </div>
          <div style={{
            fontFamily: "'Inter', sans-serif", fontSize: 20, fontWeight: 700,
            color: '#1c2033', lineHeight: 1.2, marginTop: 2,
            letterSpacing: '-0.01em',
          }}>
            {pageTitle}
          </div>
        </div>
      </div>

      {/* Right: actions */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>

        {/* Notification bell */}
        <button style={{
          width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'transparent', border: '1px solid #e3dcc4', borderRadius: 8,
          cursor: 'pointer', color: '#8a8778', transition: 'all 0.15s',
        }}
          onMouseEnter={e => {
            const el = e.currentTarget as HTMLElement;
            el.style.color = '#d97706';
            el.style.borderColor = 'rgba(217,119,6,0.3)';
            el.style.background = 'rgba(217,119,6,0.05)';
          }}
          onMouseLeave={e => {
            const el = e.currentTarget as HTMLElement;
            el.style.color = '#8a8778';
            el.style.borderColor = '#e3dcc4';
            el.style.background = 'transparent';
          }}
        >
          <svg width="15" height="15" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
          </svg>
        </button>

        {/* Divider */}
        <div style={{ width: 1, height: 24, background: '#e3dcc4', margin: '0 6px' }} />

        {/* Profile */}
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => setProfileOpen(o => !o)}
            style={{
              display: 'flex', alignItems: 'center', gap: 10,
              background: 'transparent', border: '1px solid #e3dcc4',
              borderRadius: 8, padding: '6px 12px', cursor: 'pointer',
              transition: 'all 0.15s',
            }}
            onMouseEnter={e => {
              const el = e.currentTarget as HTMLElement;
              el.style.borderColor = 'rgba(217,119,6,0.3)';
              el.style.background = 'rgba(217,119,6,0.04)';
            }}
            onMouseLeave={e => {
              const el = e.currentTarget as HTMLElement;
              el.style.borderColor = '#e3dcc4';
              el.style.background = 'transparent';
            }}
          >
            {/* Avatar */}
            <div style={{
              width: 28, height: 28, borderRadius: '50%',
              background: 'linear-gradient(135deg, #d97706, #b45309)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: "'Inter', sans-serif", fontSize: 11, fontWeight: 700, color: '#ffffff',
              flexShrink: 0,
            }}>
              {initials || '?'}
            </div>
            {/* Name */}
            <div style={{ textAlign: 'left' }}>
              <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, fontWeight: 600, color: '#1c2033', lineHeight: 1.2 }}>
                {user?.firstName} {user?.lastName}
              </div>
              <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 10, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#8a8778' }}>
                {user?.role}
              </div>
            </div>
            <svg width="10" height="10" fill="none" stroke="#8a8778" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {/* Dropdown */}
          {profileOpen && (
            <div
              style={{
                position: 'absolute', right: 0, top: 'calc(100% + 8px)',
                width: 220, zIndex: 50,
                background: '#ffffff',
                border: '1px solid #e3dcc4',
                borderRadius: 10,
                boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
              }}
            >
              {/* User info */}
              <div style={{ padding: '14px 18px', borderBottom: '1px solid #f4efe2' }}>
                <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 14, fontWeight: 700, color: '#1c2033' }}>
                  {user?.firstName} {user?.lastName}
                </div>
                <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#8a8778', marginTop: 2 }}>
                  {user?.email}
                </div>
              </div>

              {/* Links */}
              {[['Your Profile', '#'], ['Settings', '/settings/company']].map(([label, href]) => (
                <a
                  key={label}
                  href={href}
                  onClick={() => setProfileOpen(false)}
                  style={{
                    display: 'block', padding: '10px 18px',
                    fontFamily: "'Inter', sans-serif", fontSize: 13, fontWeight: 500,
                    color: '#5b5a52',
                    textDecoration: 'none', transition: 'all 0.12s',
                  }}
                  onMouseEnter={e => {
                    const el = e.currentTarget as HTMLElement;
                    el.style.color = '#d97706';
                    el.style.background = 'rgba(217,119,6,0.05)';
                  }}
                  onMouseLeave={e => {
                    const el = e.currentTarget as HTMLElement;
                    el.style.color = '#5b5a52';
                    el.style.background = 'transparent';
                  }}
                >
                  {label}
                </a>
              ))}

              {/* Logout */}
              <div style={{ borderTop: '1px solid #f4efe2' }}>
                <button
                  onClick={logout}
                  style={{
                    display: 'block', width: '100%', textAlign: 'left',
                    padding: '10px 18px', background: 'transparent',
                    fontFamily: "'Inter', sans-serif", fontSize: 13, fontWeight: 500,
                    color: '#dc2626',
                    border: 'none', cursor: 'pointer', transition: 'all 0.12s',
                    borderRadius: '0 0 10px 10px',
                  }}
                  onMouseEnter={e => {
                    const el = e.currentTarget as HTMLElement;
                    el.style.background = 'rgba(220,38,38,0.06)';
                  }}
                  onMouseLeave={e => {
                    const el = e.currentTarget as HTMLElement;
                    el.style.background = 'transparent';
                  }}
                >
                  Sign out
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};
